<template>
  <div>
    <header-component />
    <div class="px-2 py-2 w-full">
      <div class="card">
        <div class="flex justify-between items-center p-2">
          <h3 class="mb-0 w-full font-bold text-gray-800 text-md" style="text-transform: capitalize">
            Branches
          </h3>
          <base-button size="sm" type="primary" @click="openAdd">Add Branch</base-button>
        </div>

        <div v-if="loading" class="flex justify-center py-12">
          <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="min-w-full bg-white border border-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b">Name</th>
                <th class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b">Legal Name</th>
                <th class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b">Address</th>
                <th class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b">Phone</th>
                <th class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b">GST</th>
                <th class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b">Color</th>
                <th class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b">Default</th>
                <th class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b">Actions</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="branch in branches" :key="branch.id" class="hover:bg-gray-50">
                <td class="px-2 py-2 text-sm text-gray-900 border-b font-medium">
                  <span
                    class="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full"
                    :style="branch.color ? { background: branch.color, color: '#fff' } : { background: '#dbeafe', color: '#1e40af' }"
                  >
                    {{ branch.name }}
                  </span>
                </td>
                <td class="px-2 py-2 text-sm text-gray-500 border-b">{{ branch.legal_name || '—' }}</td>
                <td class="px-2 py-2 text-sm text-gray-900 border-b">{{ branch.address }}</td>
                <td class="px-2 py-2 text-sm text-gray-900 whitespace-nowrap border-b">{{ branch.phone }}</td>
                <td class="px-2 py-2 text-sm border-b">
                  <span v-if="branch.is_gst" class="text-green-700 font-medium text-xs">
                    GST: {{ branch.gst_number || '—' }}
                  </span>
                  <span v-else class="text-gray-400 text-xs">Non-GST</span>
                </td>
                <td class="px-2 py-2 border-b">
                  <span
                    v-if="branch.color"
                    class="inline-block w-6 h-6 rounded-full border border-gray-200"
                    :style="{ background: branch.color }"
                    :title="branch.color"
                  ></span>
                  <span v-else class="text-gray-400 text-xs">—</span>
                </td>
                <td class="px-2 py-2 border-b text-center">
                  <span
                    v-if="branch.is_default"
                    class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-700"
                    title="This branch is used for Excel imports"
                  >&#9733; Default</span>
                  <span v-else class="text-gray-300 text-xs">—</span>
                </td>
                <td class="px-2 py-2 text-sm whitespace-nowrap border-b">
                  <div class="flex items-center gap-2">
                    <base-button size="sm" type="info" @click="viewLedger(branch)">Ledger</base-button>
                    <base-button size="sm" type="secondary" @click="openEdit(branch)">Edit</base-button>
                    <base-button size="sm" type="danger" @click="deleteBranch(branch)">Delete</base-button>
                  </div>
                </td>
              </tr>
              <tr v-if="!branches || branches.length === 0">
                <td colspan="8" class="px-2 py-4 text-sm text-center text-gray-500 border-b">No branches found.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Add / Edit Modal -->
    <div
      v-if="showModal"
      class="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4"
    >
      <div class="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-y-auto" style="max-height: 90vh">
        <!-- Header -->
        <div class="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <h3 class="text-base font-bold text-gray-800">{{ editing ? 'Edit Branch' : 'Add Branch' }}</h3>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-600 text-lg leading-none">&#x2715;</button>
        </div>

        <div class="px-6 py-4">
          <!-- Row 1: Name + Legal Name -->
          <div class="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label class="block text-xs font-medium text-gray-600 mb-1">Name <span class="text-red-500">*</span></label>
              <input v-model="form.name" type="text" class="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" :class="{ 'border-red-400': formErrors.name }" placeholder="Branch name" />
              <p v-if="formErrors.name" class="text-red-500 text-xs mt-0.5">{{ formErrors.name }}</p>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-600 mb-1">Legal Name</label>
              <input v-model="form.legal_name" type="text" class="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" placeholder="As on GST certificate / invoice" />
            </div>
          </div>

          <!-- Row 2: Phone + GST -->
          <div class="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label class="block text-xs font-medium text-gray-600 mb-1">Phone <span class="text-red-500">*</span></label>
              <input v-model="form.phone" type="text" class="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" :class="{ 'border-red-400': formErrors.phone }" placeholder="Phone number" />
              <p v-if="formErrors.phone" class="text-red-500 text-xs mt-0.5">{{ formErrors.phone }}</p>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-600 mb-1">GST Registration</label>
              <div class="flex gap-4 mt-2">
                <label class="flex items-center gap-1.5 cursor-pointer text-sm">
                  <input type="radio" :value="false" v-model="form.is_gst" class="accent-blue-600" /> Non-GST
                </label>
                <label class="flex items-center gap-1.5 cursor-pointer text-sm">
                  <input type="radio" :value="true" v-model="form.is_gst" class="accent-blue-600" /> GST Registered
                </label>
              </div>
            </div>
          </div>

          <!-- GST Number (conditional, full width) -->
          <div v-if="form.is_gst" class="mb-3">
            <label class="block text-xs font-medium text-gray-600 mb-1">GST Number</label>
            <input v-model="form.gst_number" type="text" maxlength="20" class="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 uppercase" placeholder="e.g. 27AAAAA0000A1Z5" />
          </div>

          <!-- Address (full width) -->
          <div class="mb-3">
            <label class="block text-xs font-medium text-gray-600 mb-1">Address <span class="text-red-500">*</span></label>
            <textarea v-model="form.address" rows="2" class="w-full border border-gray-300 rounded px-3 py-1.5 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-500" :class="{ 'border-red-400': formErrors.address }" placeholder="Branch address"></textarea>
            <p v-if="formErrors.address" class="text-red-500 text-xs mt-0.5">{{ formErrors.address }}</p>
          </div>

          <!-- Row 3: Latitude + Longitude -->
          <div class="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label class="block text-xs font-medium text-gray-600 mb-1">Latitude</label>
              <input v-model="form.latitude" type="number" step="0.000001" class="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" placeholder="e.g. 18.520430" />
              <p class="text-gray-400 text-xs mt-0.5">For geofencing attendance (100m radius)</p>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-600 mb-1">Longitude</label>
              <input v-model="form.longitude" type="number" step="0.000001" class="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" placeholder="e.g. 73.856743" />
              <p class="text-gray-400 text-xs mt-0.5">&nbsp;</p>
            </div>
          </div>

          <!-- Row 4: Badge Color + Default branch -->
          <div class="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label class="block text-xs font-medium text-gray-600 mb-1">Badge Color</label>
              <div class="flex items-center gap-2">
                <input v-model="form.color" type="color" class="h-8 w-12 rounded border border-gray-300 cursor-pointer p-0.5 flex-shrink-0" />
                <input v-model="form.color" type="text" maxlength="20" class="w-28 border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" placeholder="#3b82f6" />
                <span
                  class="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full truncate max-w-20"
                  :style="form.color ? { background: form.color, color: '#fff' } : { background: '#dbeafe', color: '#1e40af' }"
                >{{ form.name || 'Preview' }}</span>
              </div>
              <p class="text-gray-400 text-xs mt-0.5">Used for branch badges across the app.</p>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-600 mb-1">Default Branch</label>
              <label class="flex items-start gap-2.5 cursor-pointer mt-1.5">
                <input type="checkbox" v-model="form.is_default" class="mt-0.5 accent-blue-600 w-4 h-4 flex-shrink-0" />
                <div>
                  <span class="text-sm text-gray-700">Set as default</span>
                  <p class="text-gray-400 text-xs mt-0.5">Excel imports will update this branch's inventory. Only one can be default.</p>
                </div>
              </label>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <base-button type="secondary" @click="closeModal" class="flex-1">Cancel</base-button>
          <base-button type="primary" @click="save" :disabled="saving" class="flex-1">
            <span v-if="saving"><span class="inline-block animate-spin mr-1">&#8987;</span> Saving...</span>
            <span v-else>Save</span>
          </base-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Api from "@/middleware/api";
import endpoints from "@/utils/constants";
import headerComponent from "../Components/HeaderComponent";

const emptyForm = () => ({
  name: "",
  legal_name: "",
  address: "",
  phone: "",
  is_gst: false,
  gst_number: "",
  color: "",
  latitude: "",
  longitude: "",
  is_default: false
});

export default {
  name: "BranchList",
  components: { "header-component": headerComponent },
  data() {
    return {
      branches: [],
      loading: false,
      showModal: false,
      editing: null,
      form: emptyForm(),
      formErrors: {},
      saving: false
    };
  },
  created() {
    this.fetchBranches();
  },
  methods: {
    async fetchBranches() {
      this.loading = true;
      try {
        const res = await new Api().call("get", `${endpoints.LARAVEL_BASE_URL}/api/v1/branches`);
        this.branches = (res && res.data && res.data.data) || [];
      } catch {
        window.notify && window.notify({ message: "Failed to load branches.", type: "danger", title: "Error" });
      } finally {
        this.loading = false;
      }
    },
    openAdd() {
      this.editing = null;
      this.form = emptyForm();
      this.formErrors = {};
      this.showModal = true;
    },
    openEdit(branch) {
      this.editing = branch;
      this.form = {
        name: branch.name || "",
        legal_name: branch.legal_name || "",
        address: branch.address || "",
        phone: branch.phone || "",
        is_gst: !!branch.is_gst,
        gst_number: branch.gst_number || "",
        color: branch.color || "",
        latitude: branch.latitude != null ? branch.latitude : "",
        longitude: branch.longitude != null ? branch.longitude : "",
        is_default: !!branch.is_default
      };
      this.formErrors = {};
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editing = null;
      this.form = emptyForm();
      this.formErrors = {};
    },
    validate() {
      const errors = {};
      if (!this.form.name || !this.form.name.trim()) errors.name = "Name is required.";
      if (!this.form.address || !this.form.address.trim()) errors.address = "Address is required.";
      if (!this.form.phone || !this.form.phone.trim()) errors.phone = "Phone is required.";
      this.formErrors = errors;
      return Object.keys(errors).length === 0;
    },
    async save() {
      if (!this.validate()) return;
      this.saving = true;
      const payload = {
        name: this.form.name.trim(),
        legal_name: this.form.legal_name ? this.form.legal_name.trim() : null,
        address: this.form.address.trim(),
        phone: this.form.phone.trim(),
        is_gst: this.form.is_gst,
        gst_number: this.form.is_gst && this.form.gst_number ? this.form.gst_number.trim().toUpperCase() : null,
        color: this.form.color ? this.form.color.trim() : null,
        is_default: !!this.form.is_default
      };
      if (this.form.latitude !== "" && this.form.latitude !== null) payload.latitude = this.form.latitude;
      if (this.form.longitude !== "" && this.form.longitude !== null) payload.longitude = this.form.longitude;

      try {
        if (this.editing) {
          await new Api().call("patch", `${endpoints.LARAVEL_BASE_URL}/api/v1/branches/${this.editing.id}`, payload);
          window.notify && window.notify({ message: "Branch updated.", type: "success", title: "Success" });
        } else {
          await new Api().call("post", `${endpoints.LARAVEL_BASE_URL}/api/v1/branches`, payload);
          window.notify && window.notify({ message: "Branch created.", type: "success", title: "Success" });
        }
        this.closeModal();
        await this.fetchBranches();
      } catch {
        window.notify && window.notify({ message: "Failed to save. Please try again.", type: "danger", title: "Error" });
      } finally {
        this.saving = false;
      }
    },
    viewLedger(branch) {
      this.$router.push("/branches/" + branch.id + "/ledger");
    },
    async deleteBranch(branch) {
      if (!window.confirm(`Delete branch "${branch.name}"?`)) return;
      try {
        await new Api().call("delete", `${endpoints.LARAVEL_BASE_URL}/api/v1/branches/${branch.id}`);
        window.notify && window.notify({ message: "Branch deleted.", type: "success", title: "Success" });
        await this.fetchBranches();
      } catch {
        window.notify && window.notify({ message: "Failed to delete.", type: "danger", title: "Error" });
      }
    }
  }
};
</script>
